import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const inputCls =
  "w-full rounded-xl border border-[#1e3d3a]/15 bg-white px-4 py-3 text-sm text-[#1e3d3a] placeholder:text-[#1e3d3a]/35 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25 transition-colors duration-300";

export default function PortalReset() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await api.post("/portal/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. Sign in with your new password.");
      setTimeout(() => nav("/portal/login", { replace: true }), 1600);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not reset password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#eae7de] text-[#1e3d3a] flex flex-col">
      <header className="max-w-3xl mx-auto w-full px-6 py-8">
        <Link to="/portal/login" data-testid="reset-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#1e3d3a]/60 hover:text-[#1e3d3a] transition-colors duration-300">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
        </Link>
      </header>
      <main className="max-w-md w-full mx-auto px-6 pb-16 flex-1 flex flex-col justify-center">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-8 space-y-5">
          <div>
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#c9a961]">Portal</span>
            <h1 className="mt-2 font-head text-3xl">Set a new password</h1>
          </div>
          {done ? (
            <div className="rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/10 px-4 py-4 text-sm flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-[#a3823f]" />
              Password updated. Sending you to sign in…
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input
                data-testid="reset-password"
                type="password"
                placeholder="New password (8+ characters)"
                className={inputCls}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                minLength={8}
              />
              {err && <p data-testid="reset-error" className="text-xs text-[#b23a48]">{err}</p>}
              <button
                data-testid="reset-submit"
                type="submit"
                disabled={busy || !token}
                className="w-full rounded-full bg-[#1e3d3a] text-white py-3 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Updating…</> : "Update password"}
              </button>
              {!token && <p className="text-xs text-[#b23a48]">Missing reset token in URL. Request a new link.</p>}
            </form>
          )}
        </motion.div>
      </main>
    </div>
  );
}
